'use client'

import { datadogRum } from '@datadog/browser-rum'
import { HOME_INDEX, isAuthedRoute, LOGIN } from '@lib/routes'
import { validateRedirectUrl } from '@lib/utils/validate-redirect-url'
import type { UserType } from '@packages/utilities/schemas'
import { setUser } from '@sentry/nextjs'
import { useQueryClient } from '@tanstack/react-query'
import { isUndefined } from 'lodash'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { environment } from 'src/environment'
import { api } from 'src/trpc/react'

interface PROVIDER_VALUES {
  isLoggedIn: boolean
  userLoading: boolean
  user: UserType | null
  fetchUser: () => void
}

const AuthContext = createContext({})

interface Properties {
  children: React.ReactNode
}

const patentPageRegex = /\/patents\/\S+/

export const AuthProvider: React.FC<Properties> = ({ children }) => {
  const router = useRouter()
  const currentRoute = usePathname()
  const urlQueryParameters = useSearchParams()
  const hasAttemptedRefresh = useRef(false)
  const queryClient = useQueryClient()

  const redirectToLogin = useCallback(() => {
    if (isAuthedRoute(currentRoute)) {
      const urlParameters = urlQueryParameters.toString()
      const currentUrl = `${currentRoute}${urlParameters ? `?${urlParameters}` : ''}`
      const loginUrl = `${LOGIN}?redirect=${encodeURIComponent(currentUrl)}`
      router.push(loginUrl)
    }
  }, [currentRoute, router, urlQueryParameters])

  const {
    data: user,
    isFetching: isUserFetching,
    isFetched: isUserFetched,
    refetch: refetchUser,
    error: userError,
  } = api.shared.user.profile.useQuery(undefined, {
    retry: false,
    enabled: false,
  })

  const { mutate: refreshUser } = api.auth.refresh.useMutation({
    onSuccess: () => refetchUser(),
    onError: () => {
      if (isAuthedRoute(currentRoute)) {
        queryClient.clear()
        redirectToLogin()
      }
    },
  })

  useEffect(() => {
    if (
      !hasAttemptedRefresh.current &&
      (isAuthedRoute(currentRoute) ||
        LOGIN === currentRoute ||
        patentPageRegex.test(currentRoute))
    ) {
      hasAttemptedRefresh.current = true
      refreshUser()
    }
  }, [currentRoute, refreshUser])

  useEffect(() => {
    if (isUserFetched && isUndefined(user)) {
      redirectToLogin()
    }

    if (!isUndefined(user) && environment.NEXT_PUBLIC_ENV !== 'development') {
      setUser({
        email: user.email,
      })
      datadogRum.setUser({
        id: user.id,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
      })
    }

    if (!isUndefined(user) && currentRoute === LOGIN) {
      const redirectParameter = urlQueryParameters.get('redirect')
      if (!redirectParameter) {
        router.push(HOME_INDEX)
        return
      }

      const validatedRedirectUrl = validateRedirectUrl(
        redirectParameter,
        window.location.hostname
      )

      if (validatedRedirectUrl) {
        router.push(validatedRedirectUrl)
        return
      } else {
        router.push(HOME_INDEX)
      }
    }
  }, [
    currentRoute,
    isUserFetched,
    redirectToLogin,
    router,
    urlQueryParameters,
    user,
  ])

  useEffect(() => {
    if (!userError) {
      return
    }

    router.refresh()
  }, [router, userError])

  const providerValues: PROVIDER_VALUES = {
    isLoggedIn: !!user,
    userLoading: isUserFetching,
    user: user ?? null,
    fetchUser: refreshUser,
  }

  return (
    <AuthContext.Provider value={providerValues}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext) as PROVIDER_VALUES
